import { em } from 'polished'
import PropTypes from 'prop-types'
import React, { Fragment } from 'react'

import PromoCodeNew from 'components/_special/PromoCodeNew'
import { UK, RU } from 'constants/countries'
import { GOODS_TYPES, COUNTRIES_WITH_DYNAMIC_DISCOUNT } from 'constants/sales'
import { LocalizationContext } from 'contexts/Localization'
import { MenuMainContext } from 'contexts/MenuMain'
import { ResizeContext } from 'contexts/Resize'
import { cartCheckout } from 'routes'
import { Col, Row } from 'styles/components/layout'
import { scrollToBlock } from 'utils/client/scroll'
import SubscriptionInfo from './components/SubscriptionInfo'

import CartAddItem from './components/CartAddItem'
import CartDeliveryAndTotal from './components/CartDeliveryAndTotal'
import CartLegend from './components/CartLegend'
import CartProductNew from './components/CartProductNew'
import {
  BoxBaseStyled,
  RowStyled,
  ButtonContent,
  ButtonRoundedStStyled,
  TextLHStyled,
  ColStyled,
  Divider,
  AddProductTitle,
} from './styles'

const { DYNAMIC_DISCOUNT_PROMO } = process.env.localization

const CartGoodsBlockNew = props => {
  const {
    cart,
    texts,
    decreaseGoods,
    increaseGoods,
    toggleSubscription,
    submitPromocode,
    closePromocode,
    isCartInitOrUpdate,
  } = props
  const { goods, isOrderCollected, sum, promo } = cart
  const { dict, paths, currentCountry, sales, locale } = React.useContext(
    LocalizationContext,
  )
  const { mobile } = React.useContext(ResizeContext)
  const { menuNode } = React.useContext(MenuMainContext)
  const topRef = React.useRef(null)
  const isDisabled = isCartInitOrUpdate || isOrderCollected

  const testsOrder = React.useMemo(() => {
    if (locale === UK) {
      return [GOODS_TYPES.biome, GOODS_TYPES.dna]
    } else if (locale === RU) {
      return [GOODS_TYPES.dna, GOODS_TYPES.biome, GOODS_TYPES.wgs]
    }

    return [GOODS_TYPES.dna, GOODS_TYPES.biome]
  }, [locale])

  const { addedTests, notAddedTests } = React.useMemo(() => {
    if (sales.goods) {
      return testsOrder.reduce(
        (acc, productType) => {
          if (sales.goods[productType].isActive) {
            if (cart.goods[productType].amount) acc.addedTests.push(productType)
            else acc.notAddedTests.push(productType)
          }
          return acc
        },
        { addedTests: [], notAddedTests: [] },
      )
    } else return { addedTests: [], notAddedTests: [] }
  }, [sales.goods, cart.goods, testsOrder])

  const addTest = React.useCallback(
    productType => () => {
      increaseGoods({
        productType,
        isSubscription: false,
      })

      scrollToBlock(topRef, menuNode)
    },
    [increaseGoods, menuNode],
  )

  const hidePromoField = React.useMemo(
    () =>
      COUNTRIES_WITH_DYNAMIC_DISCOUNT.includes(locale) &&
      sum.amount > 1 &&
      promo.name === DYNAMIC_DISCOUNT_PROMO,
    [locale, sum, promo],
  )

  const [
    subscriptionModalVisible,
    setSubscriptionModalVisibility,
  ] = React.useState(false)

  const toggleModal = newValue => setSubscriptionModalVisibility(newValue)

  return (
    <BoxBaseStyled ref={topRef}>
      <CartLegend texts={texts.legend} />
      <Row>
        <Col width={{ xs: 1 }}>
          {addedTests.map(productType => (
            <Fragment key={productType}>
              <Divider />
              <CartProductNew
                boxes={`${paths.cdn_global}/boxes/${productType}-cart-new`}
                currencyCode={sales.currency}
                dict={dict}
                productInfo={goods[GOODS_TYPES[productType]]}
                productConfig={sales.goods[GOODS_TYPES[productType]]}
                productType={GOODS_TYPES[productType]}
                precision={sales.precision}
                isСontrolsHidden={sales.isGroupon}
                handleDecrease={decreaseGoods}
                handleIncrease={increaseGoods}
                toggleSubscription={toggleSubscription}
                intlCode={currentCountry.intlCode}
                texts={texts}
                isDisabled={isDisabled}
                isCartInitOrUpdate={isCartInitOrUpdate}
                toggleModal={toggleModal}
              />
            </Fragment>
          ))}
          <Divider />
        </Col>
      </Row>
      <RowStyled>
        <Col width={{ xs: 1 }}>
          <Row>
            <ColStyled
              ml={{ md: em(143), lg: em(192) }}
              width={{ xs: 1, md: 'auto' }}
              flexGrow={{ md: 1 }}
              order={{ xs: 2, md: 0 }}
            >
              {!sales.isPromoHidden && !hidePromoField && (
                <PromoCodeNew
                  key={locale}
                  promocodeId={promo.id}
                  isFetching={promo.isFetching || isOrderCollected}
                  submitErrors={promo.errors.submitErrors}
                  errorCode={promo.errors.errorCode}
                  texts={texts.promocode}
                  buttonTitle={dict.buttons.enter}
                  inputName='promocode'
                  handleSubmit={submitPromocode}
                  handleClose={closePromocode}
                  isDisabled={sales.isPromoDisabled}
                />
              )}
            </ColStyled>
            <ColStyled
              width={{ xs: 1, md: em(335), lg: em(486) }}
              order={{ xs: 0, md: 1 }}
            >
              {cart.sum && cart.sum.amount > 0 && (
                <CartDeliveryAndTotal
                  isCartInitOrUpdate={isCartInitOrUpdate}
                  isFree={cart.sum.delivery === 0}
                  texts={texts}
                  isDisabled={isDisabled}
                  precision={sales.precision}
                  cart={cart}
                />
              )}
            </ColStyled>
            <ColStyled
              ml={{
                md: `calc(100% - ${em(335)})`,
                lg: `calc(100% - ${em(486)})`,
              }}
              mt={{ xs: em(20), md: em(35), lg: em(48) }}
              mb={{ xs: em(20), md: 0 }}
              width={{ xs: 1, md: em(335), lg: em(486) }}
              order={{ xs: 1, md: 2 }}
            >
              <ButtonRoundedStStyled
                forwardedAs='Link'
                asPath={cartCheckout.as(locale)}
                to={cartCheckout.link(locale)}
                size={mobile ? 'base' : 'medium'}
              >
                <ButtonContent>
                  <TextLHStyled
                    dangerouslySetInnerHTML={{
                      __html: dict.buttons.fill_form,
                    }}
                  />
                </ButtonContent>
              </ButtonRoundedStStyled>
            </ColStyled>
          </Row>
        </Col>
      </RowStyled>
      {!!notAddedTests.length && (
        <Row justifyContent={{ xs: 'center', md: 'flex-end' }}>
          <Col width={{ xs: 1 }}>
            <AddProductTitle
              dangerouslySetInnerHTML={{ __html: texts.add_product_title }}
            />
            {notAddedTests.map(productType => {
              const isZeroPrice =
                sales?.goods?.[productType]?.currentPrice === 0 ||
                sales?.goods?.[productType]?.priceWebsite === 0 ||
                sales?.goods?.[productType]?.price === 0

              return (
                <CartAddItem
                  key={productType}
                  boxes={`${paths.cdn_global}/boxes/${productType}-cart-new`}
                  title={dict.kits[productType].title}
                  description={
                    mobile
                      ? dict.kits[productType].uk_description_mob
                      : dict.kits[productType].uk_description
                  }
                  buttonText={
                    mobile ? dict.buttons.add_to_cart : dict.buttons.add
                  }
                  buttonTextOutOffStock={dict.buttons.out_of_stock}
                  buttonSize={mobile ? 'fit' : 'base'}
                  addItem={addTest(productType)}
                  isZeroPrice={isZeroPrice}
                />
              )
            })}
          </Col>
        </Row>
      )}
      {subscriptionModalVisible && (
        <SubscriptionInfo
          paths={paths}
          toggleModal={toggleModal}
          dict={dict.kits.biome.types.subscription}
        />
      )}
    </BoxBaseStyled>
  )
}

CartGoodsBlockNew.propTypes = {
  /** State of cart */
  cart: PropTypes.object.isRequired,

  /** Function to reset promocode state when close input */
  closePromocode: PropTypes.func.isRequired,

  /** Decrease amount of current goods in the cart */
  decreaseGoods: PropTypes.func.isRequired,

  /** Increase amount of current goods in the cart */
  increaseGoods: PropTypes.func.isRequired,

  /** Send promo code to server for check, then apply */
  submitPromocode: PropTypes.func.isRequired,

  /** Object with localized texts for cart page */
  texts: PropTypes.object.isRequired,

  /** Hide price when cart init or update  */
  isCartInitOrUpdate: PropTypes.bool,

  toggleSubscription: PropTypes.func,
}

CartGoodsBlockNew.defaultProps = {}

export default CartGoodsBlockNew
